import { useState, useEffect } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { User } from "firebase/auth";
import { useBreadcrumbItems } from "../context/BreadcrumbContext";
import { usePreviewMode } from "../context/PreviewModeContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { hasSeenGwRecap } from "../lib/gwRecapSeen";
import Logo from "./Logo";
import MarketingShell from "./MarketingShell";
import type { AuthModalMode } from "./AuthModal";

/* ── Icon SVGs ────────────────────────────────── */
const IconGrid = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="5" height="5" rx="1.5" />
    <rect x="9" y="2" width="5" height="5" rx="1.5" />
    <rect x="2" y="9" width="5" height="5" rx="1.5" />
    <rect x="9" y="9" width="5" height="5" rx="1.5" />
  </svg>
);
const IconGear = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <circle cx="8" cy="8" r="2" />
    <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.5 3.5l1.5 1.5M11 11l1.5 1.5M3.5 12.5L5 11M11 5l1.5-1.5" />
  </svg>
);
const IconUser = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="5" r="3" />
    <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" />
  </svg>
);
const IconInfo = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <circle cx="8" cy="8" r="7"/>
    <path d="M8 7v5M8 5v.5"/>
  </svg>
);
const IconSignOut = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3" />
    <path d="M11 11l3-3-3-3" />
    <path d="M14 8H6" />
  </svg>
);
function leagueDotColor(status: string): string {
  if (status === "RUNNING")   return "var(--color-success)";
  if (status === "PREPARING") return "oklch(0.80 0.15 55)";
  return "var(--color-text-dim)";
}

const SIDEBAR_W = 220;
const HEADER_H = 60;

interface Props {
  children: React.ReactNode;
  user?: User | null;
  isAdmin?: boolean;
  hasDelegateAccess?: boolean;
  previewMode?: boolean;
  displayName?: string;
  onSignIn?: (mode?: AuthModalMode) => void;
  onSignOut?: () => void;
}


function Layout({ children, user, isAdmin, hasDelegateAccess, previewMode = false, displayName, onSignIn, onSignOut }: Props) {
  const { toggle } = usePreviewMode();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbs = useBreadcrumbItems();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [totalLeagueCount, setTotalLeagueCount] = useState<number | null>(null);
  const [activeLeagues, setActiveLeagues] = useState<{ id: string; name: string; status: string; latestCompletedGwNumber: number | null }[]>([]);

  const isMarketingRoute = location.pathname === "/" || (location.pathname === "/about" && !user) || (location.pathname.startsWith("/join/") && !user);

  useEffect(() => {
    if (!user || isMarketingRoute) { setTotalLeagueCount(null); setActiveLeagues([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const token = await user.getIdToken();
        const resp = await fetch(`${process.env.REACT_APP_API_URL}/leagues`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok && !cancelled) {
          const data: any[] = await resp.json();
          const approved = data.filter((l) => l.membershipStatus === "APPROVED");
          setTotalLeagueCount(approved.length);
          setActiveLeagues(
            approved
              .filter((l) => !l.archived)
              .map((l) => ({ id: l.id, name: l.name, status: l.status ?? "PREPARING", latestCompletedGwNumber: l.latestCompletedGwNumber ?? null }))
              .sort((a, b) => a.name.localeCompare(b.name)),
          );
        }
      } catch { /* sidebar fetch failures are non-critical */ }
    };
    load();
    return () => { cancelled = true; };
  }, [user, location.pathname, isMarketingRoute]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  if (isMarketingRoute) {
    return <MarketingShell user={user} displayName={displayName} onSignIn={onSignIn}>{children}</MarketingShell>;
  }

  const activeLeagueId = location.pathname.match(/^\/leagues\/([^/]+)/)?.[1] ?? null;

  const avatarLetter = displayName ? displayName.charAt(0).toUpperCase() : "?";

  return (
    <div style={{ minHeight: "100vh", background: "var(--color-bg)" }}>

      {/* ── Fixed top header ── */}
      <header
        style={{
          position: "fixed",
          top: 0, left: 0, right: 0,
          height: HEADER_H,
          display: "flex",
          alignItems: "center",
          padding: "0 1.5rem",
          background: "var(--color-nav-bg)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--color-border)",
          zIndex: 50,
          boxSizing: "border-box",
        }}
      >
        {/* Hamburger — left side on mobile */}
        {isMobile && (
          <button
            onClick={() => setDrawerOpen((o) => !o)}
            aria-label="Open menu"
            style={{
              marginRight: "0.75rem",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "6px",
              color: "var(--color-text)",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="19" y2="6" />
              <line x1="3" y1="11" x2="19" y2="11" />
              <line x1="3" y1="16" x2="19" y2="16" />
            </svg>
          </button>
        )}

        {/* Logo */}
        <Link to="/" style={{ textDecoration: "none" }}>
          <Logo />
        </Link>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.75rem" }}>
          {user ? (
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
              {/* Avatar — click → profile */}
              <Link to="/profile" style={{ textDecoration: "none", flexShrink: 0 }}>
                <div
                  style={{
                    width: 34, height: 34, borderRadius: "50%",
                    background: previewMode ? "var(--color-text-muted)" : "var(--color-accent)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: "0.88rem",
                    color: "var(--color-btn-primary-text)",
                    boxShadow: previewMode ? "none" : "0 0 12px var(--color-accent-glow)",
                    transition: "background 0.2s, box-shadow 0.2s",
                    cursor: "pointer",
                  }}
                >
                  {avatarLetter}
                </div>
              </Link>
              {/* Name (clickable → profile) + role — stacked, hidden on mobile */}
              <div style={{ display: isMobile ? "none" : "flex", flexDirection: "column", lineHeight: 1.25 }}>
                <Link
                  to="/profile"
                  style={{ fontWeight: 600, fontSize: "0.88rem", color: "var(--color-nav-text)", textDecoration: "none" }}
                >
                  {displayName}
                </Link>
                {isAdmin ? (
                  <button
                    onClick={toggle}
                    title={previewMode ? t("nav.exitPreview") : t("nav.enterPreview")}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      textAlign: "left",
                      fontSize: "0.68rem",
                      color: previewMode ? "oklch(0.80 0.15 55)" : "var(--color-text-muted)",
                      fontFamily: "var(--font-body)",
                      fontWeight: previewMode ? 600 : 400,
                      letterSpacing: "0.02em",
                      transition: "color 0.15s",
                    }}
                  >
                    {previewMode ? `⚠ ${t("nav.memberStandard")}` : t("nav.memberAdmin")}
                  </button>
                ) : (
                  <span style={{ fontSize: "0.68rem", color: "var(--color-text-muted)" }}>
                    {t("nav.memberStandard")}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <button
              onClick={() => onSignIn?.()}
              style={{
                background: "var(--color-accent)",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                padding: "0.4rem 1rem",
                fontSize: "0.88rem",
                fontWeight: 600,
                color: "var(--color-btn-primary-text)",
              }}
            >
              {t("home.signIn")}
            </button>
          )}
        </div>
      </header>

      {/* ── Below header: sidebar + content ── */}
      <div style={{ display: "flex", marginTop: HEADER_H }}>

        {/* ── Mobile backdrop ── */}
        {isMobile && drawerOpen && (
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 45,
              background: "rgba(0,0,0,0.45)",
            }}
          />
        )}

        {/* ── Sidebar / drawer ── */}
        <aside
          style={{
            position: "fixed",
            top: isMobile ? 0 : HEADER_H,
            left: 0,
            bottom: 0,
            width: SIDEBAR_W,
            background: "var(--color-nav-bg)",
            boxShadow: isMobile ? "2px 0 16px rgba(0,0,0,0.25)" : "1px 0 0 var(--color-border)",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            zIndex: 46,
            boxSizing: "border-box",
            transform: isMobile ? (drawerOpen ? "translateX(0)" : "translateX(-100%)") : "none",
            transition: isMobile ? "transform 0.25s ease" : "none",
            paddingTop: isMobile ? HEADER_H : 0,
          }}
        >
          {/* ── Nav items ── */}
          <nav style={{ flex: 1, padding: "1rem 0.75rem", display: "flex", flexDirection: "column", gap: "2px" }}>
            {user && (
              <NavItem
                label={t("nav.profile")}
                active={location.pathname === "/profile"}
                onClick={() => navigate("/profile")}
                icon={<IconUser />}
              />
            )}

            <NavItem
              label={t("nav.overview")}
              active={location.pathname === "/overview"}
              onClick={() => navigate("/overview")}
              icon={<IconGrid />}
              badge={totalLeagueCount ?? undefined}
            />

            {/* ── Active leagues ── */}
            {user && totalLeagueCount !== null && totalLeagueCount > 0 && (
              <div style={{ marginTop: "1rem" }}>
                <span style={{
                  display: "block", fontSize: "0.65rem", fontWeight: 600,
                  textTransform: "uppercase", letterSpacing: "0.1em",
                  color: "var(--color-nav-text)", opacity: 0.5,
                  padding: "0.3rem 0.75rem 0.5rem",
                }}>
                  {t("nav.activeLeagues")}
                </span>
                <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                  {activeLeagues.map((league) => (
                    <NavItem
                      key={league.id}
                      label={league.name}
                      active={activeLeagueId === league.id}
                      onClick={() => navigate(`/leagues/${league.id}`)}
                      indent
                      dotColor={leagueDotColor(league.status)}
                      hasUpdate={!!user && league.latestCompletedGwNumber != null && !hasSeenGwRecap(user.uid, league.id, league.latestCompletedGwNumber)}
                      updateTitle={t("gwRecap.reopen")}
                    />
                  ))}
                </div>
              </div>
            )}
          </nav>

          {/* ── Bottom: admin + language + sign out ── */}
          <div
            style={{
              padding: "0.75rem",
              borderTop: "1px solid var(--color-border)",
              display: "flex",
              flexDirection: "column",
              gap: "0.25rem",
            }}
          >
            {isAdmin && !previewMode && (
              <NavItem
                label={t("nav.admin")}
                active={location.pathname.startsWith("/admin")}
                onClick={() => navigate("/admin")}
                icon={<IconGear />}
              />
            )}

            {hasDelegateAccess && (
              <NavItem
                label={t("nav.myGameweeks")}
                active={location.pathname.startsWith("/my-gameweeks")}
                onClick={() => navigate("/my-gameweeks")}
                icon={<IconGear />}
              />
            )}

            <NavItem
              label="About"
              active={location.pathname === "/about"}
              onClick={() => navigate("/about")}
              icon={<IconInfo />}
            />

            {user && onSignOut && (
              <NavItem
                label={t("nav.signOut")}
                active={false}
                onClick={onSignOut}
                icon={<IconSignOut />}
              />
            )}

          </div>
        </aside>

        {/* ── Main content area (offset by sidebar on desktop) ── */}
        <div
          style={{
            marginLeft: isMobile ? 0 : SIDEBAR_W,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: `calc(100vh - ${HEADER_H}px)`,
            minWidth: 0,
          }}
        >
          {/* Preview mode banner */}
          {previewMode && (
            <div style={{
              background: "oklch(0.76 0.15 60 / 0.1)",
              borderBottom: "1px solid oklch(0.76 0.15 60 / 0.3)",
              padding: "0.4rem 1.75rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
              flexShrink: 0,
            }}>
              <span style={{ fontSize: "0.78rem", color: "oklch(0.80 0.15 55)", fontWeight: 500 }}>
                {t("nav.previewBanner")}
              </span>
              <button
                onClick={toggle}
                style={{
                  background: "none",
                  border: "1px solid oklch(0.76 0.15 60 / 0.5)",
                  borderRadius: 5,
                  cursor: "pointer",
                  padding: "2px 8px",
                  fontSize: "0.72rem",
                  color: "oklch(0.80 0.15 55)",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {t("nav.exitPreview")}
              </button>
            </div>
          )}

          {/* Breadcrumb strip — only show when more than 1 item */}
          {breadcrumbs.length > 1 && (
            <nav
              aria-label="breadcrumb"
              style={{
                padding: "0.5rem 1.75rem",
                background: "var(--color-bg)",
                borderBottom: "1px solid var(--color-border)",
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.3rem",
                fontSize: "0.85rem",
                flexShrink: 0,
                position: "sticky",
                top: HEADER_H,
                zIndex: 30,
              }}
            >
              {breadcrumbs.map((item, i) => {
                const isLast = i === breadcrumbs.length - 1;
                return (
                  <span key={i} style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                    {i > 0 && (
                      <span style={{ color: "var(--color-text-muted)", userSelect: "none", fontSize: "0.8rem" }}>›</span>
                    )}
                    {item.to && !isLast ? (
                      <Link
                        to={item.to}
                        style={{ color: "var(--color-accent)", textDecoration: "none", fontWeight: 500 }}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <span
                        style={{ color: "var(--color-text)", fontWeight: isLast ? 700 : 500 }}
                        aria-current={isLast ? "page" : undefined}
                      >
                        {item.label}
                      </span>
                    )}
                  </span>
                );
              })}
            </nav>
          )}

          <main style={{ padding: "1.25rem 1.5rem 1.5rem", flex: 1, background: "var(--color-bg)" }}>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}

interface NavItemProps {
  label: string;
  active: boolean;
  onClick: () => void;
  indent?: boolean;
  icon?: React.ReactNode;
  dotColor?: string;
  /** Unrelated to dotColor (which signals league status, before the label) — this signals an
   * unseen gameweek recap and renders after the label so the two indicators can't collide. */
  hasUpdate?: boolean;
  updateTitle?: string;
  badge?: number;
  disabled?: boolean;
}

function NavItem({ label, active, onClick, indent = false, icon, dotColor, hasUpdate = false, updateTitle, badge, disabled = false }: NavItemProps) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      title={label}
      disabled={disabled}
      style={{
        background: active ? "var(--color-accent-glow)" : "transparent",
        border: "none",
        borderLeft: active ? "2px solid var(--color-accent)" : "2px solid transparent",
        cursor: disabled ? "default" : "pointer",
        borderRadius: "7px",
        padding: indent ? "0.45rem 0.625rem 0.45rem 0.875rem" : "0.5rem 0.625rem",
        width: "100%",
        textAlign: "left",
        fontSize: "0.84rem",
        fontWeight: active ? 600 : 400,
        color: disabled ? "var(--color-text-dim)" : active ? "var(--color-accent)" : "var(--color-text-muted)",
        opacity: disabled ? 0.45 : 1,
        overflow: "hidden",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
        transition: "background 0.1s, color 0.1s",
        marginBottom: 1,
      }}
      onMouseEnter={(e) => {
        if (!active && !disabled) {
          (e.currentTarget as HTMLButtonElement).style.background = "var(--color-surface-hover)";
          (e.currentTarget as HTMLButtonElement).style.color = "var(--color-text)";
        }
      }}
      onMouseLeave={(e) => {
        if (!active && !disabled) {
          (e.currentTarget as HTMLButtonElement).style.background = "transparent";
          (e.currentTarget as HTMLButtonElement).style.color = "var(--color-text-muted)";
        }
      }}
    >
      {icon && (
        <span style={{ flexShrink: 0, display: "flex", alignItems: "center", opacity: 0.6 }}>
          {icon}
        </span>
      )}
      {dotColor && (
        <span style={{ flexShrink: 0, width: 7, height: 7, borderRadius: "50%", background: dotColor, boxShadow: `0 0 5px ${dotColor}` }} />
      )}
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>
        {label}
      </span>
      {hasUpdate && (
        <span title={updateTitle} style={{ flexShrink: 0, width: 7, height: 7, borderRadius: "50%", background: "var(--color-accent)", boxShadow: "0 0 5px var(--color-accent)" }} />
      )}
      {badge != null && (
        <span style={{ flexShrink: 0, fontSize: "0.65rem", fontWeight: 700, background: "var(--color-surface-alt)", color: "var(--color-text-muted)", borderRadius: 10, padding: "1px 6px", border: "1px solid var(--color-border-subtle)" }}>
          {badge}
        </span>
      )}
    </button>
  );
}

export default Layout;
